import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface MagicalIconButtonProps {
  onClick?: () => void;
  brandColor: string;
  secondaryColor?: string | null;
  size?: number;
  /** Optional explicit width (defaults to `size`, giving a circle). Set wider than `size` for a pill. */
  width?: number;
  title?: string;
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
  /** Show a small text label beneath the button (used by the action row). */
  label?: string;
  /** Max-width class for the label (widen for multi-word labels like "Links & resources"). */
  labelMaxWidthClass?: string;
  /** Visually mark the button as active (e.g. muted state). */
  activeState?: "danger" | "active" | null;
  disabled?: boolean;
  /** Branded hover-popout copy ("Share this genie with others", etc.). */
  hoverHint?: string;
  /** Which edge of the button the hint anchors to. Default "center". */
  hintAlign?: "left" | "center" | "right";
  /** Hint appears below by default; set "top" to flip above for bottom-edge buttons. */
  hintPlacement?: "top" | "bottom";
  /** Idle background override (replaces the default `${brandColor}14`). Non-danger states only. */
  idleBackgroundColor?: string | null;
  /** Active/pressed-state background override (replaces the default solid brandColor). */
  activeBackgroundColor?: string | null;
  /** Icon color override (replaces brandColor for idle/hover icon color). */
  iconColorOverride?: string | null;
}

export const MagicalIconButton = ({
  onClick,
  brandColor,
  secondaryColor,
  size = 44,
  width,
  title,
  ariaLabel,
  className = "",
  children,
  label,
  labelMaxWidthClass = "max-w-[5.5rem]",
  activeState = null,
  disabled = false,
  hoverHint,
  hintAlign = "center",
  hintPlacement = "bottom",
  idleBackgroundColor,
  activeBackgroundColor,
  iconColorOverride,
}: MagicalIconButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const isDanger = activeState === "danger";
  const isActive = activeState === "active";

  const idleBg = isDanger
    ? "#FEE2E2"
    : isActive
      ? activeBackgroundColor || brandColor
      : idleBackgroundColor || `${brandColor}14`;
  const hoverBg = isDanger
    ? "#FECACA"
    : isActive
      ? activeBackgroundColor || brandColor
      : idleBackgroundColor || `${brandColor}28`;
  const iconColor = isDanger ? "#DC2626" : isActive ? "#ffffff" : iconColorOverride || brandColor;
  // When a custom idle background is set, the hover state reuses the same
  // color (no color-math lib in the repo to darken/lighten an arbitrary hex)
  // and instead gets a brightness bump for a visible hover response.
  const hoverFilter = !isDanger && !isActive && idleBackgroundColor ? "brightness(0.92)" : undefined;
  const glowShadow = isDanger
    ? `0 0 22px rgba(220,38,38,0.45), 0 0 8px rgba(220,38,38,0.35), 0 4px 14px rgba(15,23,42,0.08)`
    : `0 0 22px ${brandColor}55, 0 0 8px ${brandColor}40, 0 4px 14px rgba(15,23,42,0.08)`;

  return (
    <div
      className={`relative inline-flex flex-col items-center ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative inline-flex">
        <button
          type="button"
          onClick={onClick}
          title={title}
          aria-label={ariaLabel || title}
          disabled={disabled}
          className="relative z-10 inline-flex items-center justify-center rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            width: width ?? size,
            height: size,
            backgroundColor: hovered && !disabled ? hoverBg : idleBg,
            color: iconColor,
            boxShadow:
              hovered && !disabled
                ? glowShadow
                : `0 1px 3px rgba(15,23,42,0.06)`,
            transform: hovered && !disabled ? "scale(1.08)" : "scale(1)",
            filter: hovered && !disabled ? hoverFilter : undefined,
          }}
        >
          {children}
        </button>
      </div>
      {label && (
        <span
          className={`pointer-events-none mt-1 w-max ${labelMaxWidthClass} text-center text-[11px] font-medium leading-snug transition-colors`}
          style={{ color: hovered ? brandColor : "#7C8B9C" }}
        >
          {label}
        </span>
      )}
      {hoverHint && (
        <AnimatePresence>
          {hovered && !disabled && (
            <motion.div
              key="hover-hint"
              initial={{ opacity: 0, y: hintPlacement === "top" ? 4 : -4, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: hintPlacement === "top" ? 4 : -4, scale: 0.92 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="pointer-events-none absolute z-20 whitespace-nowrap"
              style={{
                ...(hintPlacement === "top"
                  ? { bottom: `calc(100% + 10px)` }
                  : { top: `calc(${size}px + 10px)` }),
                ...(hintAlign === "left" ? { left: 0 } : {}),
                ...(hintAlign === "right" ? { right: 0 } : {}),
                ...(hintAlign === "center" ? { left: "50%", transform: "translateX(-50%)" } : {}),
              }}
            >
              <div
                className="p-[1.5px] rounded-full"
                style={{
                  background: `linear-gradient(to right, ${brandColor}, ${secondaryColor || brandColor})`,
                  boxShadow: `0 8px 24px -10px ${brandColor}66, 0 2px 6px rgba(15,23,42,0.08)`,
                }}
              >
                <div className="rounded-full bg-white px-3 py-1.5">
                  <span className="text-[12px] font-medium text-[#222B45]">{hoverHint}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default MagicalIconButton;
